import { ChangeEvent, useEffect, useState } from 'react'
import { Grid, Pagination, Popover, Skeleton, Typography } from '@mui/material'
import axios from 'axios'
import Avatar from '../../components/Avatar'
import { LoaderContainer, ContainerTitle, CardContainer } from './styles'
import {
  Filters,
  ProviderListItem,
  useFetchProviderList,
  useFetchSpecialtyAreasList,
  useFetchSpecialtyList,
  useFilters,
} from '../../utils/hooks'
import ProviderModal from '../../components/Modal'
import FiltersHeader from '../../components/FiltersHeader'
import { ENV } from '../../constants/general'

const countPerPage = 12

const getDefaultProvider = (): ProviderListItem => ({
  providerEpicId: '',
  displayName: '',
  displayTitle: '',
  languages: [],
  image: '',
  slug: '',
  locations: [],
  serviceArea: '',
  specialties: [],
  specialtyAreaNames: '',
  specialtyAreaKeys: [],
  gender: '',
  ageRange: '',
  minAge: 0,
  maxAge: 0,
  nextVideoSlot: new Date(),
  nextOfficeSlot: new Date(),
  bio: '',
})

const ProviderList = (): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null)
  const { data: providerList, error, isLoading } = useFetchProviderList()
  const specialtyAreaList = useFetchSpecialtyAreasList()
  const specialtyList = useFetchSpecialtyList()
  const [page, setPage] = useState<number>(0)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [trackedQuery, setTrackedQuery] = useState<{
    name: string
    page: number
    filters: Filters
  } | null>(null)

  const [selectedProvider, setSelectedProvider] = useState<ProviderListItem>(
    getDefaultProvider()
  )

  const {
    filteredList,
    handleFilterChange,
    specialtyAreaOptions,
    specialtyOptions,
    languageOptions,
    locationOptions,
    nameOptions,
    titleOptions,
    filtersApplied,
  } = useFilters(providerList, specialtyAreaList, specialtyList)

  useEffect(() => {
    setPage(0)
  }, [filteredList])

  useEffect(() => {
    if (openModal) {
      setTrackedQuery({
        name: selectedProvider.displayName,
        page: page + 1,
        filters: filtersApplied || {},
      })
    } else {
      setTrackedQuery(null)
    }
  }, [openModal])

  useEffect(() => {
    const timeout = setTimeout(() => {
      const filtersNotEmpty = Object.values(filtersApplied).some(
        (filter) => filter !== ''
      )
      if (filtersNotEmpty && !filteredList.length && ENV.TRACKING_ENDPOINT) {
        axios
          .post(ENV.TRACKING_ENDPOINT, {
            query: {
              filters: filtersApplied,
            },
            found: false,
            type: 'providers',
          })
          .then(() => {})
          .catch(() => {})
      }
    }, 500)
    return () => clearTimeout(timeout)
  }, [filteredList])

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setPage(value - 1)
  }

  const handleModalToggle = (provider: ProviderListItem) => {
    const fullProvider = providerList.find((p) => p.slug === provider.slug)
    if (fullProvider) setSelectedProvider(provider)
    setOpenModal(!openModal)
  }

  const handleProviderTracking = () => {
    if (!ENV.TRACKING_ENDPOINT || !trackedQuery) {
      return
    }
    axios
      .post(ENV.TRACKING_ENDPOINT, {
        query: trackedQuery,
        found: true,
        type: 'providers',
      })
      .then(() => {})
      .catch(() => {})
  }

  return (
    <>
      <ContainerTitle variant="h3" fontWeight={500} align="center">
        Our Team
      </ContainerTitle>
      <Typography align="center" sx={{ mb: 3 }} fontSize={21} fontWeight={400}>
        As a reminder, you can always see any provider at Perlman Clinic.
        <Typography
          component="span"
          onClick={({ currentTarget }: { currentTarget: HTMLSpanElement }) =>
            setAnchorEl(currentTarget)
          }
          sx={{ ml: 1, cursor: 'pointer' }}
          color="primary"
          fontSize={21}
          fontWeight={400}
        >
          Learn More
        </Typography>
      </Typography>
      <Popover
        disablePortal
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 1,
          sx: { p: 2, maxWidth: { xs: '260px', sm: '360px' } },
        }}
      >
        <Typography>
          Patients have full access to any location and provider at Perlman
          Clinic!
        </Typography>
        <Typography>
          •{' '}
          <Typography color="primary" component="span">
            PPO insurance plans
          </Typography>
          : you can see any provider at Perlman Clinic.
        </Typography>
        <Typography>
          •{' '}
          <Typography color="primary" component="span">
            HMO insurance plans
          </Typography>
          : your insurance card likely lists a Primary Care Provider (PCP). So
          long as the PCP on your card works at Perlman Clinic, we are a
          team-based model and you can see any provider at Perlman Clinic.
        </Typography>
        <Typography>
          •{' '}
          <Typography color="primary" component="span">
            No insurance
          </Typography>
          : you can see any provider at Perlman Clinic.
        </Typography>
      </Popover>
      {!error && (
        <FiltersHeader
          handleFilterChange={handleFilterChange}
          specialtyAreaOptions={specialtyAreaOptions}
          specialtyOptions={specialtyOptions}
          languageOptions={languageOptions}
          locationOptions={locationOptions}
          nameOptions={nameOptions}
          titleOptions={titleOptions}
        />
      )}
      <CardContainer container spacing={2} justifyContent="center">
        {isLoading &&
          [...Array(12).keys()].map((load) => (
            <Grid item key={load}>
              <LoaderContainer elevation={3}>
                <Skeleton variant="circular" width={206} height={206} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </LoaderContainer>
            </Grid>
          ))}
        {!isLoading && filteredList?.length ? (
          <>
            {filteredList
              .slice(page * countPerPage, (page + 1) * countPerPage)
              .map((provider) => (
                <Grid item key={provider.providerEpicId}>
                  <Avatar
                    onAvatarClick={() => handleModalToggle(provider)}
                    image={provider.image}
                    displayName={provider.displayName}
                    languages={provider.languages}
                    specialtyAreaNames={provider.specialtyAreaNames}
                    ageRange={provider.ageRange}
                    videoSlot={provider.nextVideoSlot}
                    officeSlot={provider.nextOfficeSlot}
                  />
                </Grid>
              ))}

            {filteredList.length > countPerPage && (
              <Pagination
                count={Math.ceil(filteredList.length / countPerPage)}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
              />
            )}
          </>
        ) : null}
        {!isLoading && error && (
          <Typography align="center" variant="h6" component="p">
            There was a problem retrieving the providers list.
          </Typography>
        )}
        {!isLoading && !filteredList?.length && (
          <Typography align="center" variant="h6" component="p">
            No providers found matching the search criteria.
          </Typography>
        )}
      </CardContainer>
      <ProviderModal
        onModalToggle={handleModalToggle}
        openModal={openModal}
        setOpen={setOpenModal}
        selectedProvider={selectedProvider}
        trackOnSchedule={handleProviderTracking}
      />
    </>
  )
}

export default ProviderList
